import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { API_BASE_URL } from '../utils/api';

async function getSupabaseToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "your_unsigned_preset";
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "your_cloud_name";

function isValidJson(str) {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

// Spinner copied from KnowledgeBase
function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

export default function Profile({ updateUserMetadata }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: '', avatar_url: '', company: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [preferences, setPreferences] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [renderTime, setRenderTime] = useState(null);
  const [showRenderTime, setShowRenderTime] = useState(false);
  const renderStartRef = React.useRef(null);

  useEffect(() => {
    renderStartRef.current = performance.now();
    setLoading(true);
    setError(null);
    getSupabaseToken()
      .then(token => {
        return fetch(`${API_BASE_URL}/api/profile`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch profile');
        return res.json();
      })
      .then(data => {
        setProfile(data);
        setForm({
          name: data.name || '',
          avatar_url: data.avatar_url || '',
          company: data.company || ''
        });
        setPreferences(data.preferences ? JSON.stringify(data.preferences, null, 2) : '');
        setLoading(false);
        // Calculate render time
        const end = performance.now();
        const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
        setRenderTime(seconds);
        setShowRenderTime(true);
        setTimeout(() => setShowRenderTime(false), 5000);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handlePreferencesChange = e => {
    setPreferences(e.target.value);
  };

  const handleAvatarUpload = async e => {
    setAvatarError(null);
    setAvatarUploading(true);
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = await getSupabaseToken();
      const res = await fetch(`${API_BASE_URL}/api/profile/avatar-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (res.ok && data.avatar_url) {
        setForm(f => ({ ...f, avatar_url: data.avatar_url }));
        if (updateUserMetadata) {
          updateUserMetadata({ avatar_url: data.avatar_url });
        }
        // Update Supabase Auth user_metadata
        await supabase.auth.updateUser({ data: { avatar_url: data.avatar_url } });
        console.log('[Profile] Avatar uploaded, new avatar_url:', data.avatar_url);
      } else {
        setAvatarError(data.detail || 'Upload failed');
      }
    } catch (err) {
      setAvatarError('Upload error');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    let parsedPrefs = null;
    if (preferences.trim()) {
      if (!isValidJson(preferences)) {
        setError('Preferences must be valid JSON');
        setSaving(false);
        return;
      }
      parsedPrefs = JSON.parse(preferences);
    }
    const token = await getSupabaseToken();
    const payload = { ...form, preferences: parsedPrefs };
    console.log('[Profile] Saving profile with payload:', payload);
    fetch(`${API_BASE_URL}/api/profile/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to update profile');
        return res.json();
      })
      .then(data => {
        setProfile(data);
        setEdit(false);
        setSuccess(true);
        setPreferences(
          data.preferences ? JSON.stringify(data.preferences, null, 2) : ''
        );
        if (updateUserMetadata) {
          updateUserMetadata({
            avatar_url: data.avatar_url,
            account_name: data.name
          });
        }
        // Update Supabase Auth user_metadata if avatar_url changed
        if (data.avatar_url) {
          supabase.auth.updateUser({ data: { avatar_url: data.avatar_url } });
        }
        console.log('[Profile] Profile updated, received from backend:', data);
      })
      .catch(err => {
        setError(err.message);
      })
      .finally(() => setSaving(false));
  };

  if (loading) return <Spinner />;
  if (error) return <div className="p-4 text-center text-red-500">Error: {error}</div>;
  if (!profile) return null;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow animate-fade-in" style={{ position: 'relative' }}>
      <h1 className="text-2xl font-bold mb-6">Profile</h1>
      <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
        <div className="mb-4">
          <img
            src={form.avatar_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'}
            alt="Avatar"
            className="w-24 h-24 rounded-full object-cover border"
            onError={e => { e.target.onerror = null; e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'; }}
          />
        </div>
        {edit ? (
          <form onSubmit={handleSave} className="w-full flex flex-col gap-4">
            <div>
              <label className="block font-semibold mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="border rounded px-3 py-2 w-full"
                maxLength={64}
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Avatar URL</label>
              <input
                type="text"
                name="avatar_url"
                value={form.avatar_url}
                onChange={handleChange}
                className="border rounded px-3 py-2 w-full"
              />
              <div className="mt-2">
                <input type="file" accept="image/*" onChange={handleAvatarUpload} disabled={avatarUploading} />
                {avatarUploading && <span className="ml-2 text-blue-600">Uploading...</span>}
                {avatarError && <span className="ml-2 text-red-600">{avatarError}</span>}
              </div>
            </div>
            <div>
              <label className="block font-semibold mb-1">Company</label>
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={handleChange}
                className="border rounded px-3 py-2 w-full"
                maxLength={64}
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Preferences (JSON)</label>
              <textarea
                name="preferences"
                value={preferences}
                onChange={handlePreferencesChange}
                className="border rounded px-3 py-2 w-full font-mono"
                rows={4}
                placeholder={`{
  "theme": "dark"
}`}
              />
              {preferences && !isValidJson(preferences) && (
                <div className="text-red-600 text-sm mt-1">Invalid JSON</div>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded font-semibold" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" className="bg-gray-200 px-4 py-2 rounded font-semibold" onClick={() => { setEdit(false); setForm({ name: profile.name || '', avatar_url: profile.avatar_url || '', company: profile.company || '' }); setPreferences(profile.preferences ? JSON.stringify(profile.preferences, null, 2) : ''); }}>Cancel</button>
            </div>
          </form>
        ) : (
          <>
            <div className="mb-2 text-lg font-semibold">{profile.name || <span className="text-gray-400">No name</span>}</div>
            <div className="mb-2 text-gray-600">{profile.company || <span className="text-gray-400">No company</span>}</div>
            <button className="bg-blue-600 text-white px-4 py-2 rounded font-semibold mt-2" onClick={() => setEdit(true)}>Edit Profile</button>
          </>
        )}
        {success && <div className="mt-4 text-green-600">Profile updated!</div>}
      </div>
      {showRenderTime && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }} className="bg-gray-800 text-white text-xs px-3 py-1 rounded shadow animate-fade-in">
          Render Time: {renderTime} seconds
        </div>
      )}
    </div>
  );
} 
